'use client'

import { useLayoutEffect, useState } from 'react'

import { loadKanjiData } from '@/actions/queries/kanji/load-kanji-data'
import { type KanjiData, type KanjiStrokeData } from '@/kanjivg/types'

/**
 * Measure the total length of every SVG path in one temporary DOM
 * element: getTotalLength needs a rendered path, and one insert for the
 * whole set forces one layout instead of one per stroke (the landing's
 * ground mounts a strip on each beat, where that was 5–15 layouts before
 * the first paint).
 */
function getPathLengths(ds: string[]): number[] {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    const paths = ds.map((d) => {
        const path = document.createElementNS(
            'http://www.w3.org/2000/svg',
            'path'
        )
        path.setAttribute('d', d)
        svg.appendChild(path)
        return path
    })
    svg.style.position = 'absolute'
    svg.style.width = '0'
    svg.style.height = '0'
    svg.style.overflow = 'hidden'
    document.body.appendChild(svg)
    const lengths = paths.map((path) => path.getTotalLength())
    document.body.removeChild(svg)
    return lengths
}

/**
 * Parse an SVG path `d` attribute to extract the stroke start direction (angle in degrees).
 * Looks at the M (moveto) and first control point (C) or endpoint (L) to determine direction.
 */
function getStrokeDirection(d: string): number {
    // Extract numbers from the path data
    const numbers = d.match(/-?\d+\.?\d*/g)?.map(Number) ?? []

    if (numbers.length < 4) return 0

    // First two numbers are the M (start) point
    const startX = numbers[0]!
    const startY = numbers[1]!

    // Next two are the first control point (C) or endpoint (L)
    const nextX = numbers[2]!
    const nextY = numbers[3]!

    const angle = Math.atan2(nextY - startY, nextX - startX) * (180 / Math.PI)
    return angle
}

/**
 * Enrich raw stroke data (SVG markup + metadata) into the full KanjiData
 * shape by parsing the SVG and deriving per-stroke lengths, directions, and
 * progressive draw-in HTML. Touches `document.body` (via getPathLength) —
 * callers must only invoke this from effect/callback context, never during
 * render.
 */
function enrichKanjiData(raw: KanjiStrokeData): KanjiData {
    const parser = new DOMParser()
    const svgDoc = parser.parseFromString(raw.svg, 'image/svg+xml')
    const svgElement = svgDoc.querySelector('svg')

    if (!svgElement) {
        throw new Error('Invalid SVG content')
    }

    const pathElements = Array.from(svgElement.querySelectorAll('path'))

    if (pathElements.length === 0) {
        throw new Error('No stroke paths found in SVG')
    }

    const strokePaths = pathElements.map((p) => p.getAttribute('d') ?? '')

    const strokeLengths = getPathLengths(strokePaths)
    const strokeDirections = strokePaths.map((d) => getStrokeDirection(d))

    const progressivePaths: string[] = []
    for (let i = 0; i < pathElements.length; i++) {
        const pathsUpToStroke = pathElements.slice(0, i + 1)
        const pathsHtml = pathsUpToStroke
            .map((path) => path.outerHTML)
            .join('\n  ')
        progressivePaths.push(pathsHtml)
    }

    return {
        ...raw,
        strokePaths,
        strokeLengths,
        strokeDirections,
        progressivePaths,
        totalStrokes: pathElements.length,
    }
}

// Module-level cache of fully-enriched kanji data, keyed by the caller's
// cacheKey (default: character). Shared across every useKanji call
// site/mount for the lifetime of the tab — enrichment (DOMParser +
// path-length measurement) is pure per stroke set, so once computed it
// never needs to be redone. Callers whose stroke set is NOT the
// character's full glyph (a primitive grading a slice of its parent
// kanji's strokes) MUST pass a distinct cacheKey, or their entry would
// poison every later same-character lookup (77.1 tool vs 78 具).
const enrichedCache = new Map<string, KanjiData>()

export default function useKanji(
    character: string,
    initialData?: KanjiStrokeData | null,
    cacheKey?: string
) {
    const key = character ? (cacheKey ?? character) : ''
    const [data, setData] = useState<KanjiData | null>(() =>
        key ? (enrichedCache.get(key) ?? null) : null
    )
    const [loading, setLoading] = useState(
        () => Boolean(key) && !enrichedCache.has(key)
    )
    const [error, setError] = useState<string | null>(null)

    // Async data fetch with synchronous reset on missing input — an
    // effect, not `use(promise)`: the KanjiVG load is keyed on a prop that
    // can change inside a single render tree and the consumer
    // (ProgressiveStrokes) is not Suspense-wrapped. A layout effect, so a
    // preloaded stroke set is enriched before the first paint: the strip
    // then never shows its skeleton for a frame, and a parent measuring
    // the strip (the landing ground's camera) sees its final height on the
    // first settle rather than 30px short.
    //
    // Deps intentionally include `initialData` by object identity, even
    // though the effect only reads it as a one-shot value. This can't loop:
    // once a frame's enrichment is resolved it's stored in
    // `enrichedCache` (keyed by `key`, not by the `initialData`
    // reference), so any re-run triggered purely by `initialData` referential
    // churn from the caller lands in the cache-hit branch below, which calls
    // setState with the *same* cached object and the already-current
    // loading/error values — React bails via Object.is without a re-render,
    // so the effect has nothing left to react to on the next pass.
    useLayoutEffect(() => {
        if (!key) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- see note above the effect
            setData(null)
            setLoading(false)
            return
        }

        const cached = enrichedCache.get(key)
        if (cached) {
            setData(cached)
            setLoading(false)
            setError(null)
            return
        }

        if (initialData) {
            try {
                const enriched = enrichKanjiData(initialData)
                enrichedCache.set(key, enriched)
                setData(enriched)
                setLoading(false)
                setError(null)
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err))
                setData(null)
                setLoading(false)
            }
            return
        }

        setLoading(true)
        setError(null)

        loadKanjiData(character)
            .then((kanjiData) => {
                if (!kanjiData) {
                    setError(`No data found for character: ${character}`)
                    setData(null)
                    return
                }

                const enriched = enrichKanjiData(kanjiData)
                enrichedCache.set(key, enriched)
                setData(enriched)
            })
            .catch((err) => {
                setError(err.message)
                setData(null)
            })
            .finally(() => {
                setLoading(false)
            })
    }, [key, character, initialData])

    return { data, loading, error }
}
